import Axios from "axios";
import { getErrorMessage } from "@/utils/helper";
import {
  AxiosError,
  configResponseType,
  createPumpTokenData,
  creatorEarningsType,
  earningsType,
  followedCreatorType,
  giftReceivedType,
  giftSentType,
  giftType,
  portFolioType,
  tokenOhlcResponse,
  tokenTradeResponse,
  tokenType,
} from "@/types/global";
import { toast } from "react-toastify";
import { store } from "@/redux/store";
import { updatePriorityFeesBody } from "@/types/common";

const AxiosInstance = Axios.create({
  baseURL: process.env.NEXT_PUBLIC_WEB3_API_URL,
  // baseURL: "http://192.168.1.87:4500",
});
AxiosInstance.interceptors.request.use(
  (config) => {
    const state = store.getState();
    // Add or modify headers here
    const token = state.Auth.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export async function getConfig(): Promise<configResponseType | undefined> {
  try {
    const response = await AxiosInstance.get(`/config`);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function fetchTradeData(obj: object) {
  try {
    const response = await AxiosInstance.post(`/tokens/trade`, obj);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function fetchTradeAmount({
  trade,
  mint,
  amount,
}: {
  trade: "buy" | "sell";
  mint: string;
  amount: string;
}): Promise<number | undefined> {
  try {
    const params = {
      mint,
      [trade === "buy" ? "amountInLamports" : "tokenAmountInLamports"]: amount,
    };
    const response = await AxiosInstance.get(`/tokens/${trade}-price`, {
      params,
    });
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function fetchTokenData(
  mint: string
): Promise<tokenType | undefined> {
  try {
    const response = await AxiosInstance.get(`/tokens/` + mint);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function fetchTradeHistory(
  mint: string,
  page: number = 1
): Promise<tokenTradeResponse | undefined> {
  try {
    const response = await AxiosInstance.get(`/tokens/trade-history`, {
      params: {
        mint,
        page,
      },
    });
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function fetchTokenOhlc({
  mint,
  duration,
  from,
  to,
}: {
  mint: string;
  duration: string;
  from: number;
  to: number;
}): Promise<tokenOhlcResponse[] | undefined> {
  try {
    const response = await AxiosInstance.get(`/tokens/ohlc`, {
      params: {
        mint,
        duration,
        from,
        to,
      },
    });
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function fetchTokenGifts(): Promise<giftType[] | undefined> {
  try {
    const response = await AxiosInstance.get(`/products`);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}
export async function sendGift({
  roomId,
  mint,
  giftId,
}: {
  roomId: string;
  mint: string;
  giftId: number;
}): Promise<string | undefined> {
  try {
    const response = await AxiosInstance.post(`/products/send-gift`, {
      roomId,
      mint,
      giftId, // use this hardcoded value for now
    });
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function getUserPortFolio(
  address: string
): Promise<portFolioType[] | undefined> {
  try {
    const response = await AxiosInstance.get(`/users/portfolio/${address}`);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function getUserFollowedCreators(
  address: string,
  page: number
): Promise<followedCreatorType[] | undefined> {
  try {
    const response = await AxiosInstance.get(
      `/users/followed-creators/${address}?page=${page}`
    );
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function getUserGiftSent(
  address: string,
  page: number
): Promise<giftSentType[] | undefined> {
  try {
    const response = await AxiosInstance.get(
      `/users/gifts-sent/${address}?page=${page}`
    );
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function getUserGiftsReceived(
  address: string
): Promise<giftReceivedType[] | undefined> {
  try {
    const response = await AxiosInstance.get(
      `/users/stats/gifts-received/${address}`
    );
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}
export async function getEarnings(
  mint: string
): Promise<earningsType | undefined> {
  try {
    const response = await AxiosInstance.get(`/tokens/earnings/${mint}`);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function createPumpToken(
  data: createPumpTokenData
): Promise<string | undefined> {
  const formData = new FormData();
  [
    "name",
    "ticker",
    "description",
    "image",
    "twitter",
    "telegram",
    "website",
    "buyAmountSol",
  ].forEach((key: string) => {
    const value = data[key as keyof createPumpTokenData];
    if (value && value !== "") {
      formData.append(key, value as string | Blob);
    }
  });
  try {
    const response = await AxiosInstance.post("/tokens", formData);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}
export async function updatePriorityFees(
  body: updatePriorityFeesBody
): Promise<boolean | undefined> {
  try {
    const response = await AxiosInstance.patch(`/users/priority-fee`, body);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

export async function getCreatorEarnings(): Promise<
  creatorEarningsType | undefined
> {
  try {
    const response = await AxiosInstance.get(`/tokens/total-earnings`);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}

//tokens/creation-status

export async function getTokenCreationStatus(): Promise<
  { code: number; message: string } | undefined
> {
  try {
    const response = await AxiosInstance.get(`/tokens/creation-status`);
    return response.data;
  } catch (e) {
    const errorMsg = getErrorMessage(e as AxiosError);
    toast.error(errorMsg, {
      position: "top-right",
    });
  }
}
